import { Suspense, lazy, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import SideNav from "./components/SideNav";
import TopBar from "./components/TopBar";
import MobileTabBar from "./components/MobileNav";
import ToastHost from "./components/ToastHost";
import ErrorState from "./components/ErrorState";
import { useAppStatus } from "./hooks/useAppStatus";
import { useAuth } from "./hooks/useAuth";
import LoadingSkeleton from "./components/LoadingSkeleton";
import { resolveLoginRedirect } from "./lib/authRedirect";
import LoginPage from "./pages/LoginPage";
import OverviewPage from "./pages/OverviewPage";
import GoalsPage from "./pages/GoalsPage";

// The chart-heavy pages pull in recharts; keeping them lazy leaves the
// home screen (which draws its own SVG) on a much smaller initial bundle.
const FinancePage = lazy(() => import("./pages/FinancePage"));
// A drill-down from Фінанси, not a ninth section: the summary answers "how am
// I doing", this answers "what was that ₴1 850 on Tuesday".
const TransactionsPage = lazy(() => import("./pages/TransactionsPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const BusinessPage = lazy(() => import("./pages/BusinessPage"));
const HealthPage = lazy(() => import("./pages/HealthPage"));
const JournalingPage = lazy(() => import("./pages/JournalingPage"));
// Lazy for the same reason as Journaling: both pull in the TipTap editor.
const NotesPage = lazy(() => import("./pages/NotesPage"));
// TipTap again, plus four handwriting fonts nobody else needs to download.
const NotebooksPage = lazy(() => import("./pages/NotebooksPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));

/** Same frame `PageShell` uses, breakpoints included — a fallback on a
    different container makes the page visibly jump the moment content lands,
    whether that content is the session probe or a lazy route chunk. */
function PageFrameSkeleton() {
  return (
    <div className="w-full px-3 py-3 sm:px-6 sm:py-6 xl:px-10">
      <LoadingSkeleton variant="stats" rows={4} />
    </div>
  );
}

/**
 * App shell. Desktop: a sidebar that is always open. Phone: a top bar with the
 * burger, its drawer, and a bottom tab bar for the four sections opened daily.
 *
 * The session gate lives here, one level above everything else: nothing below
 * — the sidebar's status poll, any lazy-loaded page — ever mounts without a
 * confirmed session, so no individual screen has to think about being logged
 * out. `useAuth`'s four states map onto four things to render; only the last
 * one is the app most people mean by "Jarvis".
 */
export default function App() {
  const auth = useAuth();
  const location = useLocation();

  let content: ReactNode;
  if (auth.status === "loading") {
    content = <PageFrameSkeleton />;
  } else if (auth.status === "error") {
    // The backend may simply not be up yet — distinct from "logged out",
    // which is what `unauthenticated` means, and shown differently below.
    content = (
      <div className="grid h-full place-items-center px-4">
        <ErrorState message={auth.error ?? ""} onRetry={auth.retry} />
      </div>
    );
  } else if (auth.status === "unauthenticated") {
    content =
      location.pathname === "/login" ? (
        <LoginPage />
      ) : (
        // Remembers where the visit was headed, so login lands back there
        // instead of always on "Сьогодні" — see `resolveLoginRedirect`.
        <Navigate
          to="/login"
          replace
          state={{ from: `${location.pathname}${location.search}` }}
        />
      );
  } else if (location.pathname === "/login") {
    // Already signed in and landed on /login by hand, or just finished
    // logging in from it — either way `resolveLoginRedirect` picks up
    // whatever `from` this history entry still carries, "/" when there is
    // none.
    content = <Navigate to={resolveLoginRedirect(location.state)} replace />;
  } else {
    content = <AppShell isOwner={auth.user?.is_owner ?? false} />;
  }

  return (
    // Toasts wrap every state so even a failed session probe can report a
    // background write gone wrong (see hooks/useToast.ts).
    <ToastHost>{content}</ToastHost>
  );
}

/** The authenticated shell: chrome + the routed pages it wraps. */
function AppShell({ isOwner }: { isOwner: boolean }) {
  // Status is polled once here and handed to every navigation — each of them
  // calling the hook would multiply the request rate.
  const status = useAppStatus();

  return (
    // No `bg-bg` here: `body` already carries it, and an opaque shell would
    // paint straight over the ambient ground behind it (src/index.css).
    <div className="flex h-full text-fg">
      <SideNav status={status} />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar status={status} />
        {/* `relative`: the scroller must also be the containing block for what
            it scrolls. Otherwise an `absolute` child with no positioned
            ancestor resolves against `#root`, which sits outside this box, and
            is therefore never clipped by the `overflow-y` below — it grows the
            document instead and the page sprouts a second scrollbar. */}
        <main className="jv-pad-tabbar relative min-w-0 flex-1 overflow-y-auto md:pb-0">
          <Suspense fallback={<PageFrameSkeleton />}>
            <Routes>
              <Route path="/" element={<OverviewPage />} />
              <Route path="/finance" element={<FinancePage />} />
              <Route path="/finance/transactions" element={<TransactionsPage />} />
              <Route path="/finance/analytics" element={<AnalyticsPage />} />
              <Route
                path="/business"
                element={isOwner ? <BusinessPage /> : <Navigate to="/" replace />}
              />
              <Route path="/goals" element={<GoalsPage />} />
              <Route path="/health" element={<HealthPage />} />
              <Route path="/journal" element={<JournalingPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/notes" element={<NotesPage />} />
              <Route path="/notebooks" element={<NotebooksPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
      <MobileTabBar />
    </div>
  );
}
